import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {
  ArrowRight,
  Check,
  Coffee,
  Dumbbell,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  Square,
  Timer as TimerIcon,
} from 'lucide-react';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {EyebrowPill} from '../components/EyebrowPill';
import {TickRing} from '../components/app/TickRing';

export type WorkoutCountdownProps = {
  eyebrow: string;
  workoutType: string;
  workoutName: string;
  workSeconds: number;
  restSeconds: number;
  rounds: number;
  movement: string;
};

type Phase = 'ready' | 'work' | 'rest' | 'complete';

export const WorkoutCountdown: React.FC<WorkoutCountdownProps> = ({
  eyebrow,
  workoutType,
  workoutName,
  workSeconds,
  restSeconds,
  rounds,
  movement,
}) => {
  const frame = useCurrentFrame();
  const {fps, durationInFrames} = useVideoConfig();

  const headerOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const headerY = interpolate(frame, [0, 16], [-20, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const dialOpacity = interpolate(frame, [10, 28], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const dialScale = interpolate(frame, [10, 30], [0.85, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Original pacing: ~0.18s of video per real workout-second. Keeps the big-
  // number countdown readable. Composition isn't long enough to render every
  // round to completion — the viewer sees an authentic in-progress snapshot
  // that gets interrupted by the closing CTA reveal. Intentional.
  const READY_FRAMES = 36;
  const ROUND_FRAMES = Math.floor((workSeconds + restSeconds) * fps * 0.18);
  const PER_ROUND = Math.max(ROUND_FRAMES, 40);
  const totalRoundFrames = PER_ROUND * rounds;
  const completeAt = READY_FRAMES + totalRoundFrames;

  const phase: Phase = (() => {
    if (frame < READY_FRAMES) return 'ready';
    if (frame >= completeAt) return 'complete';
    const within = (frame - READY_FRAMES) % PER_ROUND;
    const workCut = Math.floor(PER_ROUND * (workSeconds / (workSeconds + restSeconds)));
    return within < workCut ? 'work' : 'rest';
  })();

  const currentRound = phase === 'complete'
    ? rounds
    : phase === 'ready'
      ? 0
      : Math.min(rounds, Math.floor((frame - READY_FRAMES) / PER_ROUND) + 1);

  const phaseProgress = (() => {
    if (phase === 'ready') return interpolate(frame, [0, READY_FRAMES], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    if (phase === 'complete') return 1;
    const within = (frame - READY_FRAMES) % PER_ROUND;
    const workCut = Math.floor(PER_ROUND * (workSeconds / (workSeconds + restSeconds)));
    if (phase === 'work') {
      return within / workCut;
    }
    return (within - workCut) / (PER_ROUND - workCut);
  })();

  const remainingSec = (() => {
    if (phase === 'ready') return Math.max(0, Math.ceil((READY_FRAMES - frame) / fps * 2));
    if (phase === 'complete') return 0;
    const fullSec = phase === 'work' ? workSeconds : restSeconds;
    return Math.max(0, Math.ceil(fullSec * (1 - phaseProgress)));
  })();

  const dialColor = (() => {
    if (phase === 'rest') return brand.timerRest;
    if (phase === 'complete') return brand.timerComplete;
    return brand.timerWork;
  })();

  const totalSec = (workSeconds + restSeconds) * rounds;

  // CTA fades in 90 frames before end (3s visible) so viewers actually register it.
  const ctaOpacity = interpolate(
    frame,
    [durationInFrames - 95, durationInFrames - 75],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const ctaSpring = spring({
    frame: Math.max(0, frame - (durationInFrames - 95)),
    fps,
    from: 0.86,
    to: 1,
    config: {damping: 14, stiffness: 95},
  });
  // Timer section dims (not fully fades) as CTA reveals — keeps the complete-state
  // dial visible as visual context behind the closing lockup, avoiding a blank screen.
  const timerFadeOut = interpolate(
    frame,
    [durationInFrames - 90, durationInFrames - 60],
    [1, 0.35],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  return (
    <AbsoluteFill>
      <BgGradient />
      <Audio src={staticFile('music/workout-countdown.mp3')} volume={0.4} />

      <AbsoluteFill
        style={{
          padding: '120px 60px 90px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 36,
        }}
      >
        <div
          style={{
            opacity: headerOpacity * timerFadeOut,
            transform: `translateY(${headerY}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <EyebrowPill label={eyebrow} />
          <div
            style={{
              fontFamily: brandFont,
              fontSize: 36,
              fontWeight: 800,
              color: brand.textPrimary,
              letterSpacing: 6,
              textTransform: 'uppercase',
            }}
          >
            {workoutType}
          </div>
          <div
            style={{
              fontFamily: brandFont,
              fontSize: 56,
              fontWeight: 800,
              color: dialColor,
              letterSpacing: -1,
              textShadow: `0 4px 24px ${dialColor}55`,
            }}
          >
            {workoutName}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: dialOpacity * timerFadeOut,
            transform: `scale(${dialScale})`,
            gap: 40,
          }}
        >
          <PhaseBadge phase={phase} />

          <Dial
            progress={phase === 'ready' ? 0 : phaseProgress}
            seconds={remainingSec}
            color={dialColor}
            phase={phase}
            totalSec={totalSec}
            currentRound={currentRound}
            totalRounds={rounds}
          />

          <ActionRow phase={phase} />

          <div
            style={{
              fontFamily: brandFont,
              fontSize: 28,
              fontWeight: 600,
              color: brand.textSecondary,
              letterSpacing: 1,
            }}
          >
            {movement}
          </div>
        </div>

        <div
          style={{
            opacity: ctaOpacity,
            transform: `scale(${ctaSpring})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 18,
          }}
        >
          <Img
            src={staticFile('logo.png')}
            style={{
              width: 96,
              height: 96,
              borderRadius: 22,
              boxShadow: `0 14px 44px ${brand.primary}55`,
            }}
          />
          <div
            style={{
              fontFamily: brandFont,
              fontSize: 52,
              fontWeight: 800,
              color: brand.textPrimary,
              letterSpacing: -1,
              lineHeight: 1,
            }}
          >
            Paste any workout.
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 28px',
              borderRadius: 999,
              background: brand.primary,
              boxShadow: `0 12px 36px ${brand.primary}66`,
            }}
          >
            <Sparkles size={26} color={brand.textPrimary} strokeWidth={2.4} />
            <div
              style={{
                fontFamily: brandFont,
                fontSize: 30,
                fontWeight: 700,
                color: brand.textPrimary,
                letterSpacing: 0.3,
              }}
            >
              Get the app
            </div>
            <ArrowRight size={26} color={brand.textPrimary} strokeWidth={2.6} />
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const PhaseBadge: React.FC<{phase: Phase}> = ({phase}) => {
  const label =
    phase === 'ready'
      ? 'READY'
      : phase === 'work'
        ? 'WORK'
        : phase === 'rest'
          ? 'REST'
          : 'COMPLETE';
  const color =
    phase === 'rest'
      ? brand.timerRest
      : phase === 'complete'
        ? brand.timerComplete
        : phase === 'ready'
          ? brand.textSecondary
          : brand.timerWork;

  return (
    <div
      style={{
        display: 'inline-flex',
        padding: '10px 26px',
        borderRadius: 999,
        background: `${color}22`,
        border: `1.5px solid ${color}66`,
        fontFamily: brandFont,
        fontSize: 24,
        fontWeight: 800,
        color,
        letterSpacing: 4,
      }}
    >
      {label}
    </div>
  );
};

const Dial: React.FC<{
  progress: number;
  seconds: number;
  color: string;
  phase: Phase;
  totalSec: number;
  currentRound: number;
  totalRounds: number;
}> = ({progress, seconds, color, phase, totalSec, currentRound, totalRounds}) => {
  const size = 520;
  const formatTotal = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const muted = 'rgba(255,255,255,0.5)';
  const display = phase === 'complete' ? '00' : seconds.toString().padStart(2, '0');

  return (
    <div style={{position: 'relative', width: size, height: size}}>
      <TickRing size={size} progress={progress} color={color} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 18,
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
          <TimerIcon size={26} color={muted} strokeWidth={2.4} />
          <span
            style={{
              fontFamily: brandFont,
              fontSize: 26,
              fontWeight: 700,
              color: muted,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatTotal(totalSec)}
          </span>
        </div>

        {phase === 'complete' ? (
          <Check size={140} color={color} strokeWidth={3.2} />
        ) : (
          <div
            style={{
              fontFamily: brandFont,
              fontSize: 196,
              fontWeight: 800,
              color: brand.textPrimary,
              letterSpacing: -6,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {display}
          </div>
        )}

        <div style={{display: 'flex', gap: 36}}>
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <Dumbbell size={26} color={muted} strokeWidth={2.4} />
            <span
              style={{
                fontFamily: brandFont,
                fontSize: 26,
                fontWeight: 700,
                color: muted,
              }}
            >
              {currentRound}/{totalRounds}
            </span>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <Coffee size={26} color={muted} strokeWidth={2.4} />
            <span
              style={{
                fontFamily: brandFont,
                fontSize: 26,
                fontWeight: 700,
                color: muted,
              }}
            >
              {Math.max(0, currentRound - (phase === 'rest' || phase === 'complete' ? 0 : 1))}/
              {totalRounds - 1}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionRow: React.FC<{phase: Phase}> = ({phase}) => {
  const center = (() => {
    if (phase === 'complete')
      return <Check size={42} color={brand.textPrimary} strokeWidth={2.6} />;
    if (phase === 'ready')
      return (
        <Play
          size={42}
          color={brand.textPrimary}
          strokeWidth={2.6}
          fill={brand.textPrimary}
        />
      );
    return (
      <Pause
        size={42}
        color={brand.textPrimary}
        strokeWidth={2.6}
        fill={brand.textPrimary}
      />
    );
  })();
  const centerActive = phase !== 'ready';

  return (
    <div style={{display: 'flex', gap: 36, alignItems: 'center'}}>
      <CircleButton size={64} bg="rgba(255,255,255,0.06)">
        <RotateCcw size={26} color="rgba(255,255,255,0.78)" strokeWidth={2.4} />
      </CircleButton>
      <CircleButton size={92} bg={brand.primary} glow={centerActive}>
        {center}
      </CircleButton>
      <CircleButton size={64} bg="rgba(255,255,255,0.06)">
        <Square size={22} color={brand.timerStop} strokeWidth={2.6} fill={brand.timerStop} />
      </CircleButton>
    </div>
  );
};

const CircleButton: React.FC<{
  size: number;
  bg: string;
  glow?: boolean;
  children: React.ReactNode;
}> = ({size, bg, glow, children}) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: bg,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: glow ? `0 0 30px ${brand.primary}80` : undefined,
    }}
  >
    {children}
  </div>
);
